/**
 * The rules for the bay of a lesson in the calendar event form.
 *
 * A lesson with a booking option can take "Auto" (the service picks a free bay
 * of the option) or one bay of the option. A custom lesson has no option and
 * can take "No resource" or any bay of the facility. In the form, the value
 * `null` stands for "Auto" with an option and for "No resource" without one.
 *
 * A user who cannot book a custom lesson (a scheduler, or a coach who books
 * for another coach) sees "Auto" and the bays of the option only. The same
 * applies when the option of the lesson is locked, because its payment is
 * fixed.
 */
import { RoleType, UserContextType } from "app/contexts/UserContext";

// The fields of a booking option that name its bays.
export type OptionBays = {
  id: number | null;
  orderNumber?: number | null;
  defaultRate?: boolean;
  scheduleResourceIds?: number[];
  resourceCategoryIds?: number[];
};

// The fields of a bay that the rules read.
export type BayRef = {
  id: number;
  resourceCategoryId: number;
};

export type ResourceChoice = {
  lessonRateId: number | null;
  scheduleResourceId: number | null;
};

export type ResourceChange =
  | { kind: "rate"; lessonRateId: number | null }
  | { kind: "bay"; scheduleResourceId: number | null };

export type ResourceContext<B extends BayRef = BayRef> = {
  // The booking options that the form offers.
  lessonRates: OptionBays[];
  // The active bays of the facility, in the order of the Resources page.
  bays: B[];
  // False for a user who cannot book a custom lesson.
  canBookCustom: boolean;
  // True when the form does not let the user change the option.
  optionLocked?: boolean;
};

/**
 * Tells if a booking option includes a bay. The option names single bays and
 * whole sections. An option that names no bay includes no bay.
 */
export function bayInOption(
  rate: OptionBays | null | undefined,
  bay: BayRef | null | undefined,
): boolean {
  if (!rate || !bay) {
    return false;
  }

  return (
    (rate.scheduleResourceIds ?? []).includes(bay.id) ||
    (rate.resourceCategoryIds ?? []).includes(bay.resourceCategoryId)
  );
}

/**
 * The booking option for a click on a bay column. The default option
 * of the coach wins when it includes the bay. Else the first option in admin
 * order that includes the bay. Else null, which means a custom lesson.
 */
export function defaultRateForBay<R extends OptionBays>(
  lessonRates: R[] | null | undefined,
  bay: BayRef | null | undefined,
): R | null {
  const rates = lessonRates ?? [];
  const defaultRate = rates.find((rate) => rate.defaultRate);
  if (bayInOption(defaultRate, bay)) {
    return defaultRate;
  }

  const byOrder = [...rates].sort(
    (a, b) =>
      (a.orderNumber ?? Number.MAX_SAFE_INTEGER) -
      (b.orderNumber ?? Number.MAX_SAFE_INTEGER),
  );

  return byOrder.find((rate) => bayInOption(rate, bay)) ?? null;
}

/**
 * Tells if the user can book a custom lesson (a lesson with no option) for
 * the coach. An admin can book it for every coach. A coach can book it for
 * himself only.
 */
export function canBookCustomSession(
  user: UserContextType,
  coachId: string | null | undefined,
): boolean {
  return Boolean(
    user.isAdmin ||
      user.role === RoleType.ADMIN ||
      (user.role === RoleType.COACH && coachId && coachId === user.id),
  );
}

const findRate = (context: ResourceContext, id: number | null) =>
  id == null ? undefined : context.lessonRates.find((rate) => rate.id === id);

const findBay = <B extends BayRef>(context: ResourceContext<B>, id: number | null) =>
  id == null ? undefined : context.bays.find((bay) => bay.id === id);

const canLeaveOption = (context: ResourceContext) =>
  context.canBookCustom && !context.optionLocked;

/**
 * The next values of the option and the bay after a change in the form.
 *
 * - A new option keeps the bay when the option includes it. Else the bay
 *   goes back to "Auto".
 * - "Custom" keeps the bay, because a custom lesson can take any bay.
 * - A bay of the option keeps the option.
 * - A bay outside the option makes the lesson custom and keeps the bay. The
 *   form shows no warning. A user who cannot leave the option never sees such
 *   a bay, so the change does nothing for that user.
 * - An option that is not in the list (for example an inactive option on
 *   edit) has bays the form does not know. The lesson can keep its bay or
 *   take "Auto", and nothing else.
 */
export function nextChoice(
  state: ResourceChoice,
  change: ResourceChange,
  context: ResourceContext,
): ResourceChoice {
  if (change.kind === "rate") {
    const rate = findRate(context, change.lessonRateId);
    if (!rate) {
      return { ...state, lessonRateId: change.lessonRateId };
    }

    const bay = findBay(context, state.scheduleResourceId);
    return {
      lessonRateId: change.lessonRateId,
      scheduleResourceId: bayInOption(rate, bay) ? bay.id : null,
    };
  }

  const rate = findRate(context, state.lessonRateId);
  if (!rate && state.lessonRateId != null) {
    return change.scheduleResourceId == null
      ? { ...state, scheduleResourceId: null }
      : state;
  }

  const bay = findBay(context, change.scheduleResourceId);
  if (!rate || !bay || bayInOption(rate, bay)) {
    return { ...state, scheduleResourceId: change.scheduleResourceId };
  }

  if (!canLeaveOption(context)) {
    return state;
  }

  return { lessonRateId: null, scheduleResourceId: bay.id };
}

export type ResourceMenu<B extends BayRef> = {
  // "auto" when the lesson has an option, "none" without one. The service
  // reads null as "Auto" for every lesson with an option, also for an option
  // that is not in the list.
  emptyChoice: "auto" | "none";
  bays: B[];
};

/**
 * The entries of the resource select for the current option.
 *
 * With an option: "Auto", then every bay of the facility, or the bays of the
 * option only for a user who cannot leave the option. Without an option: "No
 * resource", then every bay, or no bay for a user who cannot book a custom
 * lesson. An option that is not in the list gives "Auto" and no bay,
 * because the form does not know its bays. The bay the form holds stays in
 * the list when it is an active bay, so the select can show it on edit.
 */
export function resourceMenu<B extends BayRef>(
  state: ResourceChoice,
  context: ResourceContext<B>,
): ResourceMenu<B> {
  const hasOption = state.lessonRateId != null;
  const rate = findRate(context, state.lessonRateId);

  let bays: B[];
  if (rate) {
    bays = canLeaveOption(context)
      ? context.bays
      : context.bays.filter((bay) => bayInOption(rate, bay));
  } else if (hasOption) {
    bays = [];
  } else {
    bays = context.canBookCustom ? context.bays : [];
  }

  const held = findBay(context, state.scheduleResourceId);
  if (held && !bays.includes(held)) {
    bays = context.bays.filter((bay) => bay === held || bays.includes(bay));
  }

  // "Auto" picks a bay of the option, so an option with no bay offers
  // "No resource". An option that is not in the list keeps "Auto".
  const picksBay = hasOption && (!rate || optionHasBays(rate));
  return { emptyChoice: picksBay ? "auto" : "none", bays };
}

/** True when the option names at least one bay or section. */
export function optionHasBays(rate: OptionBays): boolean {
  return (
    (rate.scheduleResourceIds ?? []).length > 0 ||
    (rate.resourceCategoryIds ?? []).length > 0
  );
}

/**
 * The event form shows the resource row while the facility has the feature
 * on and the select has a bay to show: an active bay, or the inactive bay the
 * lesson holds. Without the row, the user cannot change or clear a held bay.
 */
/**
 * The resource row shows only when the user can pick a bay, or the lesson
 * holds one. A scheduler with an option that names no bay has nothing to
 * pick, so the row is hidden and the lesson saves with no bay.
 */
export function showResourceRow(
  featureOn: boolean,
  menuBays: BayRef[],
  heldBay: { id: number } | null,
): boolean {
  return featureOn && (menuBays.length > 0 || heldBay != null);
}
